import React, { useState, useEffect } from "react";
import TopNavBar from "./TopNavBar";

import { projectURL } from './utils/mockdata';

const API_BASE = projectURL + 'profiles/my-login-history';

const LoginHistory = (props) => {
    const [allLogs, setAllLogs] = useState([]); // full array fallback
    const loggedUserId = sessionStorage.getItem("loggedUserId");
    const [serverLogs, setServerLogs] = useState([]); // when server returns paginated docs
    const [serverPagination, setServerPagination] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [showIpModal, setShowIpModal] = useState(false);
    const [selectedIp, setSelectedIp] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendMessage, setSendMessage] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        async function fetchLogs() {
            setLoading(true);
            setError(null);
            try {
                // try asking server for paginated data
                const id = sessionStorage.getItem("loggedUserId");
                const url = `${API_BASE}/${id}?page=${page}&limit=${limit}`;
                const res = await fetch(url, { signal: controller.signal });
                if (!res.ok) throw new Error(`Server returned ${res.status}`);
                const json = await res.json();

                // Common server pagination shapes
                if (json && Array.isArray(json.docs)) {
                    setServerPagination(true);
                    setServerLogs(json.docs || []);
                    setTotal(json.totalDocs != null ? json.totalDocs : (json.total || json.count || json.docs.length));
                } else if (json && Array.isArray(json.user)) {
                    // API returns { user: [...], total, totalPages }
                    setServerPagination(false);
                    setAllLogs(json.user || []);
                    setTotal(json.total != null ? json.total : json.user.length);
                } else if (Array.isArray(json)) {
                    // server returned full array (no pagination)
                    setServerPagination(false);
                    setAllLogs(json);
                    setTotal(json.length);
                } else if (json && Array.isArray(json.data)) {
                    // sometimes wrapped in data
                    setServerPagination(false);
                    setAllLogs(Array.isArray(json.data) ? json.data : (json.data.user || []));
                    setTotal(Array.isArray(json.data) ? json.data.length : (json.data.total || (json.data.user ? json.data.user.length : 0)));
                } else {
                    // unknown shape — try to extract array fields or store as single-entry
                    const arr = Array.isArray(json) ? json : json && json.logs && Array.isArray(json.logs) ? json.logs : [];
                    if (arr.length) {
                        setServerPagination(false);
                        setAllLogs(arr);
                        setTotal(arr.length);
                    } else {
                        // fallback: store single object as one-entry array
                        setServerPagination(false);
                        setAllLogs(json ? [json] : []);
                        setTotal(json ? 1 : 0);
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError') setError(err.message || 'Failed to load');
            } finally {
                setLoading(false);
            }
        }

        fetchLogs();
        return () => controller.abort();
    }, [page, limit]);

    // detect small screens (Tailwind 'sm' breakpoint is 640px)
    useEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth < 640);
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const displayed = serverPagination
        ? serverLogs
        : allLogs.slice((page - 1) * limit, page * limit);

    const totalPages = Math.max(1, Math.ceil(total / limit));

    const formatDateTime = (when) => {
        if (!when) return '-';
        const d = new Date(when);
        if (Number.isNaN(d.getTime())) return '-';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        let hh = d.getHours();
        const ampm = hh >= 12 ? 'PM' : 'AM';
        hh = hh % 12;
        if (hh === 0) hh = 12;
        const hhs = String(hh).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        const secs = String(d.getSeconds()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hhs}:${mins}:${secs} ${ampm}`;
    };

    const openIpModal = (ip) => {
        if (!ip) return;
        setSelectedIp(ip);
        setShowIpModal(true);
    };

    const closeIpModal = () => {
        setSelectedIp(null);
        setShowIpModal(false);
    };

    const sendBlockIp = async () => {
        if (!selectedIp) return;
        setSending(true);
        setSendMessage(null);
        try {
            const url = projectURL + 'profiles/block-unknow-ip';
            const payload = {
                userId: loggedUserId,
                ipAddress: selectedIp,
                approvedByAdmin: true
            };
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            setSendMessage('Sent successfully');
            setTimeout(() => {
                setShowIpModal(false);
                setSelectedIp(null);
                setSendMessage(null);
            }, 1000);
        } catch (err) {
            setSendMessage(err.message || 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    function fmtRow(item, idx) {
        // common fields: date, ip, device, location
        const when = item.loginTime;
        const ip = item.ip || item.ipAddress || item.address;
        const device = item.device || item.userAgent || item.platform;
        const location = (item.city || item.region || item.country)
            ? [item.city, item.region, item.country].filter(Boolean).join(', ')
            : (item.region || '-');
        return (
            <tr key={idx} className="bg-white">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDateTime(when)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ip ? (<button type="button" onClick={(e) => { e.preventDefault(); openIpModal(ip); }} className="text-pink-600 hover:underline">{ip}</button>) : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{location || '-'}</td>
            </tr>
        );
    }

    function fmtCard(item, idx) {
        const when = item.loginTime;
        const ip = item.ip || item.ipAddress || item.address || '-';
        const device = item.device || item.userAgent || item.platform || '-';
        const location = (item.city || item.region || item.country)
            ? [item.city, item.region, item.country].filter(Boolean).join(', ')
            : (item.region || '-');
        return (
            <div key={idx} className="bg-white border rounded-lg p-4 mb-3 shadow-sm">
                <div className="text-sm text-gray-700"><span className="font-medium">When:</span> {formatDateTime(when)}</div>
                <div className="text-sm text-gray-600 mt-1"><span className="font-medium">IP:</span> {ip && ip !== '-' ? (<button type="button" onClick={(e) => { e.preventDefault(); openIpModal(ip); }} className="text-pink-600 hover:underline">{ip}</button>) : '-'}</div>
                <div className="text-sm text-gray-600 mt-1"><span className="font-medium">Device:</span> {device}</div>
                <div className="text-sm text-gray-600 mt-1"><span className="font-medium">Location:</span> {location}</div>
            </div>
        );
    }

    return (
        <div className="min-h-full">
            <TopNavBar />
            <main>
                <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900"> Login History </h1>
                    </div>
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-sm text-gray-600">Total: {total}</div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600">Per page:</label>
                                <select value={limit} onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }} className="border rounded px-2 py-1 text-sm">
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                        </div>

                        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                                {!isMobile ?
                                    (<thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                        </tr>
                                    </thead>) : <></>
                                }
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {!isMobile ? (
                                        loading ? (
                                            Array.from({ length: Math.min(Math.max(1, limit), 10) }).map((_, i) => (
                                                <tr key={"skeleton-" + i} className="bg-white" aria-hidden>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : error ? (
                                            <tr><td colSpan={4} className="px-6 py-4 text-sm text-red-600">{error}</td></tr>
                                        ) : displayed.length === 0 ? (
                                            <tr><td colSpan={4} className="px-6 py-4 text-sm text-gray-500">No login history found.</td></tr>
                                        ) : (
                                            displayed.map((r, i) => fmtRow(r, i + (serverPagination ? 0 : (page - 1) * limit)))
                                        )
                                    ) : (
                                        // on mobile we render an empty row; cards are rendered below
                                        <tr><td colSpan={4} className="p-0 border-0" /></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile card list */}
                        {isMobile && (
                            <div className="mt-4">
                                {loading ? (
                                    Array.from({ length: Math.min(Math.max(1, limit), 5) }).map((_, i) => (
                                        <div key={"card-skel-" + i} className="bg-white border rounded-lg p-4 mb-3 shadow-sm" aria-hidden>
                                            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse mb-3" />
                                            <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse mb-2" />
                                            <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
                                        </div>
                                    ))
                                ) : error ? (
                                    <div className="text-sm text-red-600">{error}</div>
                                ) : displayed.length === 0 ? (
                                    <div className="text-sm text-gray-500">No login history found.</div>
                                ) : (
                                    displayed.map((r, i) => fmtCard(r, i + (serverPagination ? 0 : (page - 1) * limit)))
                                )}
                            </div>
                        )}

                        <div className="text-left">
                            <small><i><b>Note:</b> If you found suspecious IP click on it & sent to Admin for Block.</i></small>
                        </div>

                        <div className="mt-4 flex items-center justify-center gap-3">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1 || loading}
                                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                aria-label="Previous page"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transform rotate-180" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.293 16.293a1 1 0 010-1.414L15.586 11H5a1 1 0 110-2h10.586l-3.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                Prev
                            </button>

                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages || loading}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                aria-label="Next page"
                            >
                                Next
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transform rotate-180" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.707 3.707a1 1 0 010 1.414L4.414 9H15a1 1 0 110 2H4.414l3.293 3.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                    </div>

                    {showIpModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                            <div className="bg-white rounded-lg shadow-lg w-full max-w-xs p-6 flex flex-col items-center text-center">
                                <div className="w-full flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mx-auto">IP Address</h3>
                                    <button onClick={closeIpModal} className="text-gray-500 hover:text-gray-700">&times;</button>
                                </div>
                                <div className="text-sm text-gray-700 break-words my-4">{selectedIp}</div>
                                <button
                                    type="button"
                                    onClick={sendBlockIp}
                                    disabled={sending}
                                    className="mt-2 text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded text-sm px-4 py-2.5 text-center leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {sending ? 'Sending...' : 'Send'}
                                </button>
                                {sendMessage && <div className="text-sm text-gray-700 mt-2">{sendMessage}</div>}
                            </div>
                        </div>
                    )}

                </div>
            </main>
        </div>
    );
}

export default LoginHistory;